import _extends from "@babel/runtime/helpers/extends";
import { RecommendationLabel } from '@wix/ambassador-premium-domains-v1-recommendation/types';
import { Box, Button, Divider, Heading, Loader, TextButton } from '@wix/design-system';
import { DomainSearchSuiteModule, SearchRecommendationLayout } from '@wix/domains-search-suite';
import { DomainsApiClient } from '@wix/domains-serverless-interface/react-kit';
import { ChevronDown } from '@wix/wix-ui-icons-common/lazy';
import React, { useState } from 'react';
import { useIsMobile } from '../../hooks/useIsMobile';
import { useIsHarmonyTheme } from '@wix/core-domain-search';
import { useMatchingLabels } from '../../hooks/useMatchingLabels';
import { useResultsPageConfig } from '../../hooks/useResultsPageConfig';
import { DomainSearchGateModule } from '../../module/domainSearchGate.module';
import { ExactMatchHeadline } from '../ExactMatchHeadline';
import { HeroCard, HeroCardSkeleton } from '../HeroCard';
import { HeroBusinessEmailExample } from '../HeroCard/HeroBusinessEmailExample';
import { LazyRecommendationLabel } from '../LazyRecommedationLabel/LazyRecommendationLabel';
import { AnimatedBanner } from '../AnimatedBanner';
import { GoogleWorkspaceBanner } from '../GoogleWorkspaceBanner';
import { createLeanLabelContainer, createOptimizedLabelContainer } from './suggestionLabelContainers';
import { RecommendationsDataHooks } from './dataHooks';
import s from './Recommendations.css';
export const Recommendations = _ref => {
  var _recommendations$, _recommendations$2, _mainCardItems$;
  let {
    onInsightHoverChange,
    recommendations,
    isLoading,
    hasMore,
    fetchNextPage,
    isFetchingNextPage,
    getItButtonOverride,
    onItemClick,
    onAiInsightsClick,
    showHeroCard = false,
    promotedLabels = [RecommendationLabel.EXACT_MATCH],
    useCompactRecommendationsLayout: useCompactRecommendationsLayoutProp = false,
    isLeanView = false,
    hideAlternativesHeading = false,
    hideFreeWithSitePlanBadge = false,
    pricingOptimized,
    isBusinessEmail = false,
    isPricingLoading = false,
    banner,
    suppressHarmonyCardStyling = false,
    desktopContentGap = '10px',
    insightsPaddingTop = 'SP2',
    compactPriceLabel = false,
    stackedMobileCards = false,
    Layouts: layoutsOverride,
    PriceNote,
    priceNoteApplies,
    ...props
  } = _ref;
  // Business email results reuse the optimized results layout (bordered
  // suggestions box, optimized hero + labels) plus the Google Workspace content.
  const useCompactRecommendationsLayout = useCompactRecommendationsLayoutProp || isBusinessEmail;
  const leanView = isLeanView || useCompactRecommendationsLayout;
  const {
    data: purchasePermissions
  } = DomainsApiClient.hooks.useGetPurchasePermissions({});
  const {
    t
  } = DomainSearchGateModule.internals.useTranslations();
  const {
    resolveActionForSuggestion
  } = DomainSearchSuiteModule.SDK.useSuggestionActionResolver();
  const isMobile = useIsMobile();
  const isHarmonyTheme = useIsHarmonyTheme();
  const {
    labels: matchLabels,
    isLoading: isLoadingLabels
  } = useMatchingLabels(recommendations, isBusinessEmail && !showHeroCard ? false : leanView);
  const {
    showExactMatchHeadline: isExactMatchHeadlineVisible,
    isV4
  } = useResultsPageConfig();
  const titleOverflowStrategy = isV4 ? 'scale' : 'ellipsis';
  const [isMouseInList, setIsMouseInList] = useState(false);
  const useStackedMobileCards = stackedMobileCards && isMobile;
  const handleGetItClick = recommendation => {
    const position = recommendations == null ? void 0 : recommendations.findIndex(_ref2 => {
      let {
        id
      } = _ref2;
      return id === recommendation.id;
    });
    DomainSearchSuiteModule.internals.bi.onGetItClick == null || DomainSearchSuiteModule.internals.bi.onGetItClick({
      domain: recommendation.domain || '',
      type: recommendation.label,
      position
    });
  };
  const onClickGetIt = async recommendation => {
    handleGetItClick(recommendation);
    if (recommendation.domain) {
      await resolveActionForSuggestion(recommendation.domain, recommendation.premium);
    }
  };
  const onClickCard = onItemClick ?? (isV4 ? onClickGetIt : undefined);
  const hasExactMatchTop = ((_recommendations$ = recommendations[0]) == null ? void 0 : _recommendations$.label) === RecommendationLabel.EXACT_MATCH;
  const topLabel = (_recommendations$2 = recommendations[0]) == null ? void 0 : _recommendations$2.label;
  const isPromotedHeroTop = !!topLabel && promotedLabels.includes(topLabel);
  const heroRecommendation = showHeroCard && isPromotedHeroTop ? recommendations[0] : undefined;
  const isExactMatchHero = (heroRecommendation == null ? void 0 : heroRecommendation.label) === RecommendationLabel.EXACT_MATCH;
  const businessEmailFirstRec = isBusinessEmail && !showHeroCard && !isLoading && recommendations.length > 0 ? recommendations[0] : undefined;
  const businessEmailRest = !!businessEmailFirstRec;
  const compactExactMatch = useCompactRecommendationsLayout && !showHeroCard && !businessEmailFirstRec && hasExactMatchTop && !isLoading ? recommendations[0] : undefined;
  const listItems = heroRecommendation || compactExactMatch || businessEmailFirstRec ? recommendations.slice(1) : recommendations;
  const showMainCards = useCompactRecommendationsLayout && !businessEmailFirstRec && !heroRecommendation && !compactExactMatch && !isLoading && listItems.length >= 2;
  const mainCardCount = useStackedMobileCards ? 1 : 2;
  const mainCardItems = showMainCards ? listItems.slice(0, mainCardCount) : [];
  const mainCardLabels = ((_mainCardItems$ = mainCardItems[0]) == null ? void 0 : _mainCardItems$.label) === RecommendationLabel.GREAT_ALTERNATIVE ? [RecommendationLabel.GREAT_ALTERNATIVE, 'RECOMMENDED'] : [RecommendationLabel.BEST_MATCH, RecommendationLabel.GREAT_ALTERNATIVE];
  const listItemsToRender = showMainCards ? listItems.slice(mainCardCount) : listItems;
  const showHeroListStyling = showHeroCard && (isLoading || !!heroRecommendation);
  const showAlternativesHeading = !hideAlternativesHeading && !isBusinessEmail && (!leanView || useCompactRecommendationsLayout) && !!heroRecommendation && !isLoading && listItems.length > 0 && !isMobile;
  const hasLeadingCard = !!heroRecommendation || !!compactExactMatch || showMainCards || !!businessEmailFirstRec;
  // An explicit banner is not tied to a leading card — it must survive the
  // hero-less states (taken domain, list-only results) where the marquee hides.
  const showProvidedBanner = !!banner && !isBusinessEmail && !isLoading && recommendations.length > 0;
  const showAnimatedBanner = hasLeadingCard && !isBusinessEmail && !banner && useCompactRecommendationsLayout;
  const recommendationList = /*#__PURE__*/React.createElement(DomainSearchSuiteModule.SDK.Components.SearchRecommendationList, {
    dataHook: RecommendationsDataHooks.list,
    onHoverChange: onInsightHoverChange,
    disabled: !(purchasePermissions != null && purchasePermissions.hasPermission),
    onClickGetIt: onClickGetIt,
    onItemClick: onClickCard,
    isLeanView: businessEmailRest ? false : leanView,
    useCompactSuggestionRowStyle: businessEmailRest ? false : useCompactRecommendationsLayout,
    pricingOptimized: pricingOptimized,
    isPricingLoading: isPricingLoading,
    compactPriceLabel: compactPriceLabel,
    titleOverflowStrategy: titleOverflowStrategy,
    Layouts: {
      ...SearchRecommendationLayout,
      ...layoutsOverride,
      LabelContainer: businessEmailRest ? LazyRecommendationLabel({
        isLoading: isLoadingLabels,
        matchLabels
      }) : useCompactRecommendationsLayout ? createOptimizedLabelContainer() : leanView ? createLeanLabelContainer({
        listItems,
        heroRecommendation,
        hideFreeWithSitePlanBadge,
        recommendations
      }) : LazyRecommendationLabel({
        isLoading: isLoadingLabels,
        matchLabels
      }),
      ...(getItButtonOverride && {
        GetItButton: getItButtonOverride
      }),
      ...(showHeroListStyling && !useCompactRecommendationsLayout && {
        HoverBox: _ref3 => {
          let {
            children,
            className: _className,
            ...rest
          } = _ref3;
          return /*#__PURE__*/React.createElement(Box, _extends({}, rest, {
            style: {
              cursor: 'pointer'
            }
          }), children);
        },
        Divider: () => /*#__PURE__*/React.createElement(Divider, null),
        GetItButton: _ref4 => {
          let {
            children,
            priority,
            ...rest
          } = _ref4;
          return /*#__PURE__*/React.createElement(Button, _extends({
            size: "medium",
            skin: "dark"
          }, rest, {
            className: s.GetItButton,
            priority: isMouseInList ? priority : 'secondary'
          }), children);
        }
      }),
      ...(businessEmailRest && {
        Divider: () => /*#__PURE__*/React.createElement(Divider, null),
        HoverBox: _ref5 => {
          let {
            children,
            className: _className,
            ...rest
          } = _ref5;
          return /*#__PURE__*/React.createElement(Box, _extends({}, rest, {
            className: s.businessEmailRow
          }), children);
        },
        GetItButton: _ref6 => {
          let {
            children,
            priority: _priority,
            className: _className,
            ...rest
          } = _ref6;
          return /*#__PURE__*/React.createElement(Button, _extends({
            size: "medium",
            skin: "dark",
            priority: "secondary"
          }, rest, {
            className: s.businessEmailGetIt
          }), children);
        }
      }),
      ...(isHarmonyTheme && !suppressHarmonyCardStyling && {
        SearchRecommendationContainer: _ref7 => {
          let {
            className,
            ...rest
          } = _ref7;
          return /*#__PURE__*/React.createElement(SearchRecommendationLayout.SearchRecommendationContainer, _extends({}, rest, {
            backgroundColor: "white",
            className: [s.suggestionRowCard, className].filter(Boolean).join(' ')
          }));
        },
        SearchRecommendationSkeletonContainer: _ref8 => {
          let {
            className,
            ...rest
          } = _ref8;
          return /*#__PURE__*/React.createElement(SearchRecommendationLayout.SearchRecommendationSkeletonContainer, _extends({}, rest, {
            backgroundColor: "white",
            className: [s.suggestionRowCard, className].filter(Boolean).join(' ')
          }));
        }
      })
    },
    items: listItemsToRender,
    isLoading: isLoading,
    useMobileLayout: isMobile,
    onAiInsightsClick: onAiInsightsClick,
    firstSuggestionPrimary: !useCompactRecommendationsLayout && !heroRecommendation,
    defaultNonHovered: useCompactRecommendationsLayout
  });
  return /*#__PURE__*/React.createElement(Box, _extends({
    dataHook: RecommendationsDataHooks.component,
    flex: 1,
    direction: "vertical",
    padding: isMobile ? 'SP1 SP4 0' : '0 48px',
    gap: showHeroListStyling && !useCompactRecommendationsLayout ? 'SP6' : undefined
  }, props), /*#__PURE__*/React.createElement(Box, {
    direction: "vertical"
  }, isExactMatchHero && (heroRecommendation == null ? void 0 : heroRecommendation.domain) && !isMobile && isExactMatchHeadlineVisible && /*#__PURE__*/React.createElement(ExactMatchHeadline, {
    domain: heroRecommendation.domain
  }), (showHeroCard || useCompactRecommendationsLayout) && isLoading && !heroRecommendation && /*#__PURE__*/React.createElement(HeroCardSkeleton, {
    optimizedSkeletonLayout: useCompactRecommendationsLayout,
    perception: Boolean(PriceNote)
  }), heroRecommendation && /*#__PURE__*/React.createElement(HeroCard, {
    recommendation: heroRecommendation,
    onClickGetIt: onClickGetIt,
    onItemClick: onClickCard,
    onAiInsightsClick: onAiInsightsClick,
    disabled: !(purchasePermissions != null && purchasePermissions.hasPermission),
    useCompactHeroCardLayout: useCompactRecommendationsLayout,
    titleOverflowStrategy: titleOverflowStrategy,
    desktopContentGap: desktopContentGap,
    insightsPaddingTop: insightsPaddingTop,
    compactPriceLabel: compactPriceLabel,
    inlineMobileActions: leanView,
    stackedMobileLayout: useStackedMobileCards,
    businessEmailExample: isBusinessEmail,
    Label: layoutsOverride == null ? void 0 : layoutsOverride.Label,
    PriceNote: PriceNote,
    priceNoteApplies: priceNoteApplies,
    isPricingLoading: isPricingLoading
  }), compactExactMatch && /*#__PURE__*/React.createElement(HeroCard, {
    recommendation: compactExactMatch,
    onClickGetIt: onClickGetIt,
    onItemClick: onClickCard,
    disabled: !(purchasePermissions != null && purchasePermissions.hasPermission),
    useCompactHeroCardLayout: true,
    compact: true,
    stackedMobileLayout: useStackedMobileCards,
    businessEmailExample: isBusinessEmail,
    Label: layoutsOverride == null ? void 0 : layoutsOverride.Label,
    PriceNote: PriceNote,
    priceNoteApplies: priceNoteApplies,
    dataHook: "exact-match-card",
    isPricingLoading: isPricingLoading,
    compactPriceLabel: compactPriceLabel
  }), showMainCards && /*#__PURE__*/React.createElement(Box, {
    direction: isMobile ? 'vertical' : 'horizontal',
    gap: "16px",
    marginTop: isMobile ? undefined : '16px'
  }, mainCardItems.map((rec, index) => /*#__PURE__*/React.createElement(HeroCard, {
    key: rec.id ?? rec.domain,
    recommendation: rec,
    onClickGetIt: onClickGetIt,
    onItemClick: onClickCard,
    disabled: !(purchasePermissions != null && purchasePermissions.hasPermission),
    useCompactHeroCardLayout: true,
    compact: true,
    stackedMobileLayout: useStackedMobileCards,
    badgeLabel: mainCardLabels[index],
    ctaPriority: index === 0 ? 'primary' : 'secondary',
    businessEmailExample: isBusinessEmail,
    Label: layoutsOverride == null ? void 0 : layoutsOverride.Label,
    PriceNote: PriceNote,
    priceNoteApplies: priceNoteApplies,
    dataHook: `main-card-${index}`,
    isPricingLoading: isPricingLoading,
    compactPriceLabel: compactPriceLabel
  }))), businessEmailFirstRec && /*#__PURE__*/React.createElement(Box, {
    borderRadius: isHarmonyTheme ? undefined : '12px',
    border: isHarmonyTheme ? undefined : '1px solid var(--wds-color-border-disabled, #e5e5e5)',
    className: isHarmonyTheme ? undefined : s.suggestionsContainer
  }, /*#__PURE__*/React.createElement(DomainSearchSuiteModule.SDK.Components.SearchRecommendationList, {
    disabled: !(purchasePermissions != null && purchasePermissions.hasPermission),
    onClickGetIt: onClickGetIt,
    onItemClick: onClickCard,
    items: [businessEmailFirstRec],
    useMobileLayout: isMobile,
    firstSuggestionPrimary: true,
    defaultNonHovered: true,
    isPricingLoading: isPricingLoading,
    compactPriceLabel: compactPriceLabel,
    Layouts: {
      ...SearchRecommendationLayout,
      ...layoutsOverride,
      ...(getItButtonOverride && {
        GetItButton: getItButtonOverride
      }),
      HoverBox: _ref9 => {
        let {
          children,
          className: _className,
          ...rest
        } = _ref9;
        return /*#__PURE__*/React.createElement(Box, rest, children);
      },
      TitleDisplay: titleProps => /*#__PURE__*/React.createElement(Box, {
        direction: "vertical",
        gap: "2px"
      }, /*#__PURE__*/React.createElement(SearchRecommendationLayout.TitleDisplay, titleProps), /*#__PURE__*/React.createElement(Box, {
        paddingLeft: businessEmailFirstRec.label === RecommendationLabel.EXACT_MATCH ? 'SP4' : undefined
      }, /*#__PURE__*/React.createElement(HeroBusinessEmailExample, {
        domain: businessEmailFirstRec.domain ?? '',
        size: "small"
      })))
    }
  })), hasLeadingCard && isBusinessEmail && /*#__PURE__*/React.createElement(GoogleWorkspaceBanner, null), showAnimatedBanner && /*#__PURE__*/React.createElement(AnimatedBanner, null), showProvidedBanner && /*#__PURE__*/React.createElement(Box, {
    margin: "20px 0"
  }, banner)), /*#__PURE__*/React.createElement("div", {
    onMouseEnter: showHeroListStyling ? () => setIsMouseInList(true) : undefined,
    onMouseLeave: showHeroListStyling ? () => setIsMouseInList(false) : undefined
  }, showAlternativesHeading && /*#__PURE__*/React.createElement(Box, {
    marginTop: "18px",
    marginBottom: useCompactRecommendationsLayout ? '32px' : undefined
  }, /*#__PURE__*/React.createElement(Heading, {
    size: "medium",
    as: "h2"
  }, t('domain_search_gate.results.alternative_suggestion_list.title'))), useCompactRecommendationsLayout ? /*#__PURE__*/React.createElement(Box, {
    borderRadius: isHarmonyTheme && !suppressHarmonyCardStyling ? undefined : '12px',
    border: isHarmonyTheme && !suppressHarmonyCardStyling ? undefined : '1px solid var(--wds-color-border-disabled, #e5e5e5)',
    className: isHarmonyTheme && !suppressHarmonyCardStyling ? undefined : s.suggestionsContainer
  }, recommendationList) : recommendationList), hasMore && /*#__PURE__*/React.createElement(Box, {
    flex: 1,
    align: "center",
    padding: "SP8 SP4 SP10 SP4"
  }, /*#__PURE__*/React.createElement(TextButton, {
    skin: "dark",
    onClick: () => fetchNextPage(),
    suffixIcon: isFetchingNextPage ? /*#__PURE__*/React.createElement(Box, {
      marginLeft: "3px",
      verticalAlign: "middle"
    }, /*#__PURE__*/React.createElement(Loader, {
      size: "tiny"
    })) : /*#__PURE__*/React.createElement(ChevronDown, null)
  }, t('domain_search_gate.results.show_more'))));
};
//# sourceMappingURL=Recommendations.js.map